// src/components/opportunity/OpportunityGeneratedTab.tsx
//
// Generated outputs tab. Lists every PDF the document package generator
// has produced for this opportunity (proposals and contracts), newest
// first. Download mints a fresh signed URL on click (list URLs expire)
// so a stale tab still opens the file. Generation itself happens in the
// workspace shell's Save control so it's available from any tab.

import { useCallback, useEffect, useState } from "react";
import type { Opportunity, OpportunityRevision } from "@/types/opportunity";
import { useAuth } from "@/contexts/AuthContext";
import {
  deleteOpportunityGeneratedOutput,
  listOpportunityGeneratedOutputs,
  type OpportunityGeneratedOutput,
} from "@/lib/opportunity/opportunityGeneratedClient";
import { userCanDeleteGeneratedOutput } from "@/lib/opportunity/opportunityGeneratedDeleteAccess";
import { formatOpportunityCurrency } from "@/lib/opportunity/formatCurrency";
import { OPPORTUNITY_OUTPUT_TYPE_LABELS } from "@/types/opportunity";
import { SIGNED_CONTRACT_SLOT_KEY } from "@/lib/opportunity/signedContractDocumentSlot";

export type OpportunityGeneratedTabProps = {
  opportunity: Opportunity;
  revisions: OpportunityRevision[];
  /** Bumped externally after a successful generation so the list re-pulls. */
  refreshKey?: number;
  /** Jump to Documents with a suggested signed-contract slot (and optional source link). */
  onUploadSigned?: (args: {
    generatedOutputId: string;
    suggestedSlotKey: string;
  }) => void;
};

export function OpportunityGeneratedTab(props: OpportunityGeneratedTabProps) {
  const { opportunity, revisions, refreshKey, onUploadSigned } = props;
  const auth = useAuth();
  const [outputs, setOutputs] = useState<OpportunityGeneratedOutput[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  const canDeleteOutput = useCallback(
    (output: OpportunityGeneratedOutput) =>
      auth?.status === "ready" &&
      auth.session?.user?.id != null &&
      userCanDeleteGeneratedOutput(
        auth.session.user.id,
        (...r) => auth.hasRole(...r),
        output,
      ),
    [auth],
  );

  async function handleDeleteOutput(outputId: string) {
    if (!window.confirm("Delete this generated document? This cannot be undone.")) return;
    setError(null);
    const r = await deleteOpportunityGeneratedOutput(outputId);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setOutputs((prev) => prev.filter((o) => o.id !== outputId));
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listOpportunityGeneratedOutputs({
      opportunityId: opportunity.id,
      signMedia: true,
    }).then((r) => {
      if (cancelled) return;
      if (r.ok) {
        setOutputs(r.data.outputs ?? []);
      } else {
        setError(r.error);
        setOutputs([]);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [opportunity.id, reloadTick, refreshKey]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-600 dark:text-zinc-400">
          Generated document packages (created artifacts). After a contract is
          signed externally, use <strong>Upload signed</strong> to attach the
          PDF under Documents. Use <strong>Save proposal</strong> /{" "}
          <strong>Save contract</strong> in the toolbar to produce a new package
          against the active pricing revision.
        </p>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="px-2.5 py-1 rounded-lg border border-gray-300 text-xs text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100">
          {error}
        </div>
      ) : null}

      {outputs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">
          No documents generated yet for this opportunity.
        </div>
      ) : (
        <ul className="space-y-2">
          {outputs.map((o) => (
            <GeneratedRow
              key={o.id}
              output={o}
              revisions={revisions}
              canDelete={canDeleteOutput(o)}
              onDelete={() => void handleDeleteOutput(o.id)}
              onUploadSigned={
                o.output_type === "contract_package" && onUploadSigned
                  ? () =>
                      onUploadSigned({
                        generatedOutputId: o.id,
                        suggestedSlotKey: SIGNED_CONTRACT_SLOT_KEY,
                      })
                  : undefined
              }
              onDownload={async () => {
                setError(null);
                const r = await listOpportunityGeneratedOutputs({
                  opportunityId: opportunity.id,
                  signMedia: true,
                });
                if (!r.ok) {
                  setError(r.error);
                  return;
                }
                const fresh = (r.data.outputs ?? []).find((row) => row.id === o.id);
                setOutputs(r.data.outputs ?? []);
                if (!fresh?.signed_url) {
                  setError("Download link unavailable — try Refresh.");
                  return;
                }
                window.open(fresh.signed_url, "_blank", "noopener,noreferrer");
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function GeneratedRow(props: {
  output: OpportunityGeneratedOutput;
  revisions: OpportunityRevision[];
  canDelete: boolean;
  onDelete: () => void;
  onDownload: () => Promise<void>;
  onUploadSigned?: () => void;
}) {
  const { output, revisions, canDelete, onDelete, onDownload, onUploadSigned } = props;
  const rev = revisions.find((r) => r.id === output.revision_id);
  const pricing = output.pricing_summary;
  const titleLabel = OPPORTUNITY_OUTPUT_TYPE_LABELS[output.output_type] ?? "Proposal package";
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadClick() {
    setDownloading(true);
    try {
      await onDownload();
    } finally {
      setDownloading(false);
    }
  }

  return (
    <li className="rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900 min-w-0 overflow-hidden">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-gray-900 dark:text-zinc-100 text-sm">
            {titleLabel}
            {pricing ? (
              <span className="ml-2 text-xs font-normal text-gray-600 dark:text-zinc-300">
                {formatOpportunityCurrency(pricing.total_sale_price)} total
                <span className="mx-1 text-gray-400 dark:text-zinc-500">·</span>
                {formatOpportunityCurrency(pricing.deposit_amount)} deposit
              </span>
            ) : null}
            {rev ? (
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-zinc-400">
                Revision #{rev.revision_number}
              </span>
            ) : null}
            {output.alternate_label ? (
              <span className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-xs font-normal text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200">
                {output.alternate_label}
              </span>
            ) : output.alternate_key ? (
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-zinc-400">
                {output.alternate_key}
              </span>
            ) : null}
          </div>
          <div className="text-xs text-gray-500 dark:text-zinc-400">
            Generated {formatTimestamp(output.generated_at)}
            {output.generated_by_name ? (
              <>
                {" "}
                by <span className="text-gray-700 dark:text-zinc-300">{output.generated_by_name}</span>
              </>
            ) : null}
            <span className="mx-1">·</span>
            {humanBytes(output.byte_size)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => void handleDownloadClick()}
            disabled={downloading}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
          >
            {downloading ? "Preparing…" : "Download"}
          </button>
          {onUploadSigned ? (
            <button
              type="button"
              onClick={onUploadSigned}
              className="px-2.5 py-1.5 rounded-lg border border-indigo-300 text-xs text-indigo-800 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-200 dark:hover:bg-indigo-950/40"
              title="Open Documents to upload the customer-signed PDF for this package"
            >
              Upload signed
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              onClick={onDelete}
              className="px-2.5 py-1.5 rounded-lg border border-red-300 text-xs text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
            >
              Delete
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso ?? "";
  return d.toLocaleString();
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
